import { useEffect, useState } from 'react';

import { iCompany } from '../components/type';
import CreditsService from '../services/api/creditsService';
import { FamlaServices } from '../services';
import { useAppSelector } from '../stores';

type Options = {
    /** Charge la liste des compagnies (modal changement de compagnie). */
    includeMyCompanies?: boolean;
};

/** Header / sidebar context: company, approvals, credits, companies list (aligned with Home). */
export function useHeaderLayoutCompanyData(options: Options = {}) {
    const { includeMyCompanies = true } = options;
    const users = useAppSelector((state) => state.auth);
    const companyId = users.session?.company_id;
    const [requestApproval, setRequestApproval] = useState<any[]>([]);
    const [isLoadingApproval, setIsLoadingApproval] = useState(true);
    const [companyById, setCompanyById] = useState<iCompany | undefined>(undefined);
    const [myCompanies, setMyCompanies] = useState<iCompany[]>([]);
    const [creditsAvailable, setCreditsAvailable] = useState<number | null>(null);
    const [creditsLoading, setCreditsLoading] = useState(false);

    useEffect(() => {
        if (!users.access_token) return;

        let cancelled = false;

        const loadCompanyById = async () => {
            if (!companyId) return;
            try {
                const data = await FamlaServices(
                    'api_host',
                    `/company/by/id/${companyId}`,
                    'GET',
                    '',
                    users.access_token,
                );
                if (!cancelled && data.status === 200 && data.body?.data) {
                    setCompanyById(data.body.data);
                }
            } catch {
                /* silent */
            }
        };

        const loadApprovalList = async () => {
            try {
                const data = await FamlaServices('api_host', `/approval/`, 'GET', '', users.access_token);
                if (cancelled) return;
                if (data.status === 200) {
                    setRequestApproval(Array.isArray(data.body?.data) ? data.body.data : []);
                }
            } catch {
                /* silent */
            } finally {
                if (!cancelled) setIsLoadingApproval(false);
            }
        };

        const loadMyCompanies = async () => {
            try {
                const data = await FamlaServices('api_host', '/company/my', 'GET', '', users.access_token);
                if (!cancelled && data.status === 200 && Array.isArray(data.body?.data)) {
                    setMyCompanies(data.body.data);
                }
            } catch {
                /* silent */
            }
        };

        const loadCredits = async () => {
            if (!companyId) return;
            setCreditsLoading(true);
            try {
                const response = await CreditsService.getCreditBalance(companyId, users.access_token);
                if (cancelled) return;
                if (response.success && response.data) {
                    setCreditsAvailable(response.data.credits_available ?? 0);
                } else {
                    setCreditsAvailable(null);
                }
            } catch {
                if (!cancelled) setCreditsAvailable(null);
            } finally {
                if (!cancelled) setCreditsLoading(false);
            }
        };

        void loadCompanyById();
        void loadApprovalList();
        if (includeMyCompanies) {
            void loadMyCompanies();
        }
        void loadCredits();

        return () => {
            cancelled = true;
        };
    }, [companyId, users.access_token, includeMyCompanies]);

    return {
        loadcompany: true as const,
        listApproval: requestApproval,
        loading: isLoadingApproval,
        companyById,
        myCompanies,
        creditsAvailable,
        creditsLoading,
        companyId,
        accessToken: users.access_token,
    };
}
